import { useApolloClient } from '@apollo/client';
import {
  ArticleDeleteMutation,
  ArticleNodeFragment,
  ArticleNodeFragmentDoc,
  Category,
  useArticleDeleteMutation,
} from 'lib-knowledge-management-common/graphql';
import { ampli } from 'lib-shared-tracking';

type MutationFunction = ReturnType<typeof useArticleDeleteMutation>[0];
type MutationReturnValue = ReturnType<MutationFunction>;
type DeleteFn = (data: { articleId: string }) => MutationReturnValue;
type UseArticleDeleteReturnValue = [
  DeleteFn,
  ReturnType<typeof useArticleDeleteMutation>[1],
];

export function useArticleDelete(): UseArticleDeleteReturnValue {
  const [deleteMutation, mutationResult] = useArticleDeleteMutation();
  const { cache } = useApolloClient();
  const deleteFn: DeleteFn = ({ articleId }) => {
    const article = cache.readFragment<ArticleNodeFragment>({
      fragment: ArticleNodeFragmentDoc,
      id: cache.identify({
        __typename: 'Article',
        id: articleId,
      }),
    });
    if (!article) throw new Error('Missing article');

    const optimisticResponse = createOptimisticResponse({
      article,
    });

    return deleteMutation({
      variables: {
        id: articleId,
      },
      optimisticResponse,

      update: (_cache) => {
        // Remove the article from its parent
        if (article.parent?.id) {
          _cache.modify<Category>({
            id: _cache.identify(article.parent),
            fields: {
              articles(existingChildRefs, { readField }) {
                return existingChildRefs.filter((childRef) => {
                  return article.id !== readField('id', childRef!);
                });
              },
            },
          });
        }

        // Clean up the article from cache
        _cache.evict({
          id: cache.identify(article),
        });
      },
    }).then((args) => {
      ampli.knowledgeArticleDestroyed({ articleId });
      return args;
    });
  };

  return [deleteFn, mutationResult];
}

function createOptimisticResponse({
  article,
}: {
  article: ArticleNodeFragment;
}): ArticleDeleteMutation {
  const mutation: ArticleDeleteMutation = {
    __typename: 'Mutations',
    articleDelete: article.id,
  };

  return mutation;
}
